/**
 * Estimate network connection speed
 * @returns {Object} { effectiveType, downlink, rtt, speedCategory }
 */
export function getNetworkInfo() {
  // Check if Network Information API is available
  const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;

  if (connection) {
    const info = {
      effectiveType: connection.effectiveType, // '4g', '3g', '2g', 'slow-2g'
      downlink: connection.downlink, // Mbps
      rtt: connection.rtt, // Round-trip time in ms
      saveData: connection.saveData, // User requested data saving mode
    };

    // Categorize speed
    let speedCategory = 'unknown';
    if (connection.effectiveType === '4g' || connection.downlink > 5) {
      speedCategory = 'fast';
    } else if (connection.effectiveType === '3g' || connection.downlink > 1) {
      speedCategory = 'medium';
    } else {
      speedCategory = 'slow';
    }

    return { ...info, speedCategory };
  }

  return {
    effectiveType: 'unknown',
    downlink: null,
    rtt: null,
    saveData: false,
    speedCategory: 'unknown'
  };
}

/**
 * Suggest compression quality based on network speed
 * @returns {number} Suggested compression quality (0.1-1.0)
 */
export function getSuggestedCompressionQuality() {
  const networkInfo = getNetworkInfo();

  // If user has enabled data saver mode, use lower quality
  if (networkInfo.saveData) {
    return 0.4;
  }

  switch (networkInfo.speedCategory) {
    case 'fast':
      return 0.8; // High quality for fast connections
    case 'medium':
      return 0.6; // Medium quality
    case 'slow':
      return 0.3; // Low quality for slow connections
    default:
      return 0.6; // Default medium quality
  }
}

/**
 * Monitor network speed changes
 * @param {Function} callback - Called when connection changes
 * @returns {Function} Cleanup function to remove listener
 */
export function monitorNetworkSpeed(callback) {
  const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;

  if (!connection) {
    return () => {};
  }

  const handleChange = () => {
    const info = getNetworkInfo();
    callback(info);
  };

  connection.addEventListener('change', handleChange);

  // Return cleanup function
  return () => {
    connection.removeEventListener('change', handleChange);
  };
}
